import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { getApiErrorMessage } from '../lib/api';
import { authService } from '../services/authService';
import AuthShell from '../components/auth/AuthShell';
import GoogleSignInButton, { GOOGLE_CLIENT_ID } from '../components/auth/GoogleSignInButton';
import axios from 'axios';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, googleLogin } = useAuthStore();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email) {
      newErrors.email = t('errors.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('auth.invalidEmail');
    }

    if (!formData.password) {
      newErrors.password = t('auth.passwordRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setShowResendVerification(false);
    setResendMessage('');

    try {
      await login(formData.email, formData.password);
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: getApiErrorMessage(error, t('auth.loginError')) });
      // 403 is only ever returned by login for one reason: the account's
      // email isn't verified yet (see AuthService.login) — offer a resend
      // right here instead of sending the user off to a separate page.
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        setShowResendVerification(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = async (idToken: string) => {
    setIsLoading(true);
    setErrors({});
    setShowResendVerification(false);
    try {
      await googleLogin(idToken);
    } catch (error) {
      console.error('Google login error:', error);
      setErrors({ general: getApiErrorMessage(error, t('auth.loginError')) });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) return;
    setIsResending(true);
    setResendMessage('');
    try {
      await authService.resendVerification(formData.email);
      setResendMessage(t('auth.resendVerificationSuccessMessage'));
    } catch (error) {
      setResendMessage(getApiErrorMessage(error, t('auth.resendVerificationError')));
    } finally {
      setIsResending(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <AuthShell title={t('auth.login')} subtitle={t('auth.loginSubtitle')}>
        {/* Form */}
        <form className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8" onSubmit={handleSubmit}>
          {errors.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 rounded-xl p-4 space-y-2">
              <p className="text-sm text-red-600 dark:text-red-400">{errors.general}</p>
              {showResendVerification && (
                <div>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={isResending}
                    className="text-sm font-medium text-brand-700 hover:text-brand-600 disabled:opacity-50"
                  >
                    {isResending ? t('auth.verifyingEmail') : t('auth.resendVerificationButton')}
                  </button>
                  {resendMessage && (
                    <p className="mt-1 text-sm text-green-600 dark:text-green-400">{resendMessage}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('auth.email')}
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`input-field ${
                    errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  placeholder={t('auth.emailPlaceholder')}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('auth.password')}
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`input-field pr-10 ${
                    errors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  placeholder={t('auth.passwordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="rememberMe"
                name="rememberMe"
                type="checkbox"
                checked={formData.rememberMe}
                onChange={handleInputChange}
                className="h-4 w-4 text-brand-600 focus:ring-brand-400 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded"
              />
              <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                {t('auth.rememberMe')}
              </label>
            </div>
            <div className="text-sm">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="font-medium text-brand-700 hover:text-brand-600"
              >
                {t('auth.forgotPassword')}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-brand text-sm"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-ink"></div>
              ) : (
                <>
                  <Lock className="h-5 w-5 mr-2" />
                  {t('auth.login')}
                </>
              )}
            </button>
          </div>

          {/* Google Sign-In — hidden entirely if REACT_APP_GOOGLE_CLIENT_ID
              isn't configured for this build. */}
          {GOOGLE_CLIENT_ID && (
            <div className="space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500">
                    {t('auth.orContinueWith')}
                  </span>
                </div>
              </div>
              <GoogleSignInButton onCredential={handleGoogleCredential} />
            </div>
          )}

          {/* Sign Up Link */}
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('auth.noAccount')}{' '}
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="font-medium text-brand-700 hover:text-brand-600"
              >
                {t('auth.signup')}
              </button>
            </p>
          </div>
        </form>
    </AuthShell>
  );
};

export default Login;
